if (!customElements.get('fidlock-variant-grid')) {
  class FidlockVariantGrid extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';

      this.variantPicker = this.closest('variant-picker');
      this.styleOptionName = this.dataset.styleOption;
      this.sizeOptionName = this.dataset.sizeOption;

      this.variantButtons = Array.from(
        this.querySelectorAll('[data-fidlock-variant]')
      );

      this.selectedText = this.querySelector(
        '[data-fidlock-selected-text]'
      );

      if (
        !this.variantPicker ||
        !this.styleOptionName ||
        !this.sizeOptionName
      ) {
        return;
      }

      this.variantData = this.getVariantData();

      this.normaliseVisibleLabels();
      this.addCustomStyles();
      this.applyCardPricing();

      this.handleClick = this.handleClick.bind(this);
      this.handleVariantChange =
        this.handleVariantChange.bind(this);

      this.addEventListener(
        'click',
        this.handleClick
      );

      this.variantPicker.addEventListener(
        'on:variant:change',
        this.handleVariantChange
      );

      const initiallySelectedButton =
        this.variantButtons.find(
          (button) =>
            button.getAttribute('aria-pressed') === 'true'
        );

      const startingButton =
        initiallySelectedButton ||
        this.variantButtons[0];

      if (startingButton) {
        this.updateSelectedState(
          startingButton.dataset.variantId
        );

        window.requestAnimationFrame(() => {
          this.selectVariant(startingButton);
        });
      }
    }

    disconnectedCallback() {
      if (this.handleClick) {
        this.removeEventListener(
          'click',
          this.handleClick
        );
      }

      if (
        this.variantPicker &&
        this.handleVariantChange
      ) {
        this.variantPicker.removeEventListener(
          'on:variant:change',
          this.handleVariantChange
        );
      }

      delete this.dataset.initialized;
    }

    getVariantData() {
      const dataElement =
        this.variantPicker.querySelector(
          'script[type="application/json"]'
        );

      if (!dataElement) return null;

      try {
        return JSON.parse(
          dataElement.textContent
        );
      } catch (error) {
        console.warn(
          'The Fidlock variant picker could not read the Shopify variant data.',
          error
        );

        return null;
      }
    }

    stripHtml(value) {
      const temporaryElement =
        document.createElement('div');

      temporaryElement.innerHTML =
        value || '';

      return (
        temporaryElement.textContent ||
        temporaryElement.innerText ||
        ''
      ).trim();
    }

    normaliseVisibleLabels() {
      const capacityLabels =
        this.querySelectorAll(
          '.fidlock-picker__capacity'
        );

      capacityLabels.forEach((label) => {
        label.textContent =
          label.textContent
            .trim()
            .replace(/ML\b/g, 'ml');
      });
    }

    addCustomStyles() {
      const existingStyles =
        document.getElementById(
          'fidlock-variant-custom-styles'
        );

      if (existingStyles) {
        existingStyles.remove();
      }

      const styleElement =
        document.createElement('style');

      styleElement.id =
        'fidlock-variant-custom-styles';

      styleElement.textContent = `
        .fidlock-picker {
          margin-top: 1.4rem !important;
        }

        .fidlock-picker__title {
          margin-bottom: 0.95rem !important;
          font-size: 1.35rem !important;
        }

        .fidlock-picker__group + .fidlock-picker__group {
          margin-top: 1rem !important;
        }

        .fidlock-picker__legend {
          gap: 0.5rem !important;
          margin-bottom: 0.5rem !important;
        }

        .fidlock-picker__capacity {
          font-size: 1.15rem !important;
          text-transform: none !important;
        }

        .fidlock-picker__descriptor {
          font-size: 0.95rem !important;
        }

        .fidlock-picker__selection {
          display: none !important;
        }

        .fidlock-picker__cards {
          display: grid !important;
          padding: 0 !important;
          overflow: visible !important;
          grid-auto-flow: row !important;
          align-items: start !important;
        }

        .fidlock-picker__card {
          position: relative;
          min-width: 0;
          min-height: 0 !important;
          height: auto !important;
          align-self: start !important;
        }

        .fidlock-picker__media {
          position: relative;
          aspect-ratio: 1 / 1.08 !important;
          overflow: hidden;
          background: #ffffff !important;
          border-radius: 5px;
        }

        .fidlock-picker__media img {
          position: absolute;
          inset: 0;
          display: block;
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
          object-position: center 25% !important;
          transform: none !important;
        }

        .fidlock-picker__name {
          margin: 0 !important;
          overflow: hidden;
          font-weight: 700;
          white-space: normal;
          overflow-wrap: normal !important;
        }

        .fidlock-picker__price {
          display: flex !important;
          flex-direction: column;
          align-items: flex-start;
          width: 100%;
          gap: 0;
          margin: 0 !important;
          padding: 0 !important;
        }

        .fidlock-picker__price-current {
          display: inline-block;
          color: rgba(0, 0, 0, 0.65);
          font-weight: 400;
          line-height: 1.15;
          white-space: nowrap;
        }

        .fidlock-picker__price--sale
        .fidlock-picker__price-current {
          color: #e52b36;
          font-weight: 800;
        }

        .fidlock-picker__price-was {
          display: inline-block;
          color: rgba(0, 0, 0, 0.5);
          font-weight: 400;
          line-height: 1.15;
          text-decoration: line-through;
          text-decoration-thickness: 1px;
          white-space: nowrap;
        }

        .fidlock-picker__sale-badge {
          position: absolute !important;
          z-index: 4;
          display: inline-flex;
          width: max-content;
          align-items: center;
          justify-content: center;
          color: #000000;
          font-weight: 800;
          line-height: 1;
          white-space: nowrap;
          background: #00bfd3;
          border-radius: 4px;
          pointer-events: none;
        }

        /*
          DESKTOP
        */
        @media (min-width: 750px) {
          .fidlock-picker__cards {
            grid-template-columns:
              repeat(3, minmax(0, 1fr)) !important;
            gap: 0.6rem !important;
            padding-top: 0.25rem !important;
          }

          /*
            Allow the discount tab to sit outside
            the top edge of the card.
          */
          .fidlock-picker__card {
            display: grid !important;
            grid-template-columns:
              4.5rem minmax(0, 1fr);
            grid-template-rows:
              auto auto;
            column-gap: 0.65rem;
            row-gap: 0.12rem;
            align-items: center;
            padding: 0.5rem !important;
            overflow: visible !important;
          }

          .fidlock-picker__media {
            grid-column: 1;
            grid-row: 1 / span 2;
            width: 4.5rem !important;
            height: auto !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          .fidlock-picker__name {
            grid-column: 2;
            grid-row: 1;
            align-self: end;
            display: -webkit-box !important;
            min-height: 0 !important;
            font-size: 0.96rem !important;
            line-height: 1.08 !important;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
          }

          .fidlock-picker__price {
            grid-column: 2;
            grid-row: 2;
            align-self: start;
            margin-top: 0.1rem !important;
          }

          .fidlock-picker__price-current {
            font-size: 0.92rem;
          }

          .fidlock-picker__price--sale {
            gap: 0.08rem;
            padding-right: 0 !important;
          }

          .fidlock-picker__price--sale
          .fidlock-picker__price-current {
            font-size: 0.94rem;
          }

          .fidlock-picker__price-was {
            font-size: 0.7rem;
          }

          /*
            Desktop discount label becomes a tab
            attached to the top-right border.

            It no longer sits anywhere near the prices.
          */
          .fidlock-picker__sale-badge {
            top: 0 !important;
            right: 0.55rem !important;
            bottom: auto !important;
            transform: translateY(-50%);
            padding: 0.23rem 0.36rem;
            font-size: 0.65rem;
          }

          /*
            If the discounted card is also selected,
            move the badge left so the tick has its
            own space in the top-right.
          */
          .fidlock-picker__card[aria-pressed='true']
          .fidlock-picker__sale-badge {
            right: 2.25rem !important;
          }

          .fidlock-picker__check {
            top: 0.38rem !important;
            right: 0.38rem !important;
            width: 1.45rem !important;
            height: 1.45rem !important;
            font-size: 0.86rem !important;
          }
        }

        /*
          MOBILE

          UNCHANGED from the version you approved.
        */
        @media (max-width: 749px) {
          .fidlock-picker {
            margin-top: 1.1rem !important;
          }

          .fidlock-picker__title {
            margin-bottom: 0.7rem !important;
            font-size: 1.16rem !important;
          }

          .fidlock-picker__group + .fidlock-picker__group {
            margin-top: 0.8rem !important;
          }

          .fidlock-picker__legend {
            margin-bottom: 0.38rem !important;
          }

          .fidlock-picker__capacity {
            font-size: 0.98rem !important;
          }

          .fidlock-picker__descriptor {
            font-size: 0.84rem !important;
          }

          .fidlock-picker__cards {
            grid-template-columns:
              repeat(3, minmax(0, 1fr)) !important;
            gap: 0.36rem !important;
          }

          .fidlock-picker__card {
            display: flex !important;
            flex-direction: column;
            align-items: stretch;
            padding: 0.36rem !important;
            overflow: hidden !important;
          }

          .fidlock-picker__media {
            width: 70% !important;
            height: auto !important;
            align-self: center;
            margin: 0 auto 0.3rem !important;
            padding: 0 !important;
          }

          .fidlock-picker__name {
            display: block !important;
            min-height: 0 !important;
            font-size: 0.76rem !important;
            line-height: 1.06 !important;
            margin-bottom: 0.28rem !important;
          }

          .fidlock-picker__price {
            margin-top: 0 !important;
          }

          .fidlock-picker__price-current {
            font-size: 0.74rem;
          }

          .fidlock-picker__price--sale {
            gap: 0.07rem;
            padding-right: 2.8rem !important;
          }

          .fidlock-picker__price--sale
          .fidlock-picker__price-current {
            font-size: 0.78rem;
          }

          .fidlock-picker__price-was {
            font-size: 0.6rem;
          }

          /*
            Keep the approved mobile badge position.
          */
          .fidlock-picker__sale-badge {
            top: auto !important;
            right: 0.32rem !important;
            bottom: 0.38rem !important;
            transform: none !important;
            padding: 0.17rem 0.24rem;
            font-size: 0.51rem;
          }

          .fidlock-picker__check {
            top: 0.24rem !important;
            right: 0.24rem !important;
            width: 1.15rem !important;
            height: 1.15rem !important;
            font-size: 0.67rem !important;
          }
        }

        @media (max-width: 360px) {
          .fidlock-picker__cards {
            gap: 0.28rem !important;
          }

          .fidlock-picker__card {
            padding: 0.3rem !important;
          }

          .fidlock-picker__media {
            width: 68% !important;
          }

          .fidlock-picker__name {
            font-size: 0.7rem !important;
            margin-bottom: 0.24rem !important;
          }

          .fidlock-picker__price-current {
            font-size: 0.69rem;
          }

          .fidlock-picker__price--sale
          .fidlock-picker__price-current {
            font-size: 0.73rem;
          }

          .fidlock-picker__price-was {
            font-size: 0.56rem;
          }

          .fidlock-picker__sale-badge {
            right: 0.28rem !important;
            bottom: 0.32rem !important;
            padding: 0.15rem 0.2rem;
            font-size: 0.47rem;
          }
        }
      `;

      document.head.appendChild(
        styleElement
      );
    }

    applyCardPricing() {
      if (
        !this.variantData ||
        !Array.isArray(
          this.variantData.variants
        )
      ) {
        return;
      }

      this.variantButtons.forEach(
        (button) => {
          const variantId = String(
            button.dataset.variantId
          );

          const variant =
            this.variantData.variants.find(
              (item) =>
                String(item.id) === variantId
            );

          const formatted =
            this.variantData.formatted &&
            this.variantData.formatted[
              variantId
            ]
              ? this.variantData.formatted[
                  variantId
                ]
              : null;

          const priceElement =
            button.querySelector(
              '.fidlock-picker__price'
            );

          if (!variant || !priceElement) {
            return;
          }

          const currentPriceHtml =
            formatted && formatted.price
              ? formatted.price
              : priceElement.innerHTML.trim();

          const compareAtPriceHtml =
            formatted &&
            formatted.compareAtPrice
              ? formatted.compareAtPrice
              : '';

          const numericPrice =
            Number(variant.price);

          const numericCompareAtPrice =
            Number(
              variant.compare_at_price
            );

          const isOnSale =
            Number.isFinite(
              numericCompareAtPrice
            ) &&
            numericCompareAtPrice >
              numericPrice &&
            numericCompareAtPrice > 0;

          const existingBadge =
            button.querySelector(
              '.fidlock-picker__sale-badge'
            );

          if (existingBadge) {
            existingBadge.remove();
          }

          priceElement.innerHTML = '';

          priceElement.classList.remove(
            'fidlock-picker__price--sale'
          );

          if (
            !button.dataset.baseAriaLabel
          ) {
            button.dataset.baseAriaLabel =
              button.getAttribute(
                'aria-label'
              ) || '';
          }

          const currentPriceElement =
            document.createElement('span');

          currentPriceElement.className =
            'fidlock-picker__price-current';

          currentPriceElement.innerHTML =
            currentPriceHtml;

          priceElement.appendChild(
            currentPriceElement
          );

          if (!isOnSale) {
            button.classList.remove(
              'fidlock-picker__card--sale'
            );

            button.setAttribute(
              'aria-label',
              button.dataset.baseAriaLabel
            );

            return;
          }

          button.classList.add(
            'fidlock-picker__card--sale'
          );

          priceElement.classList.add(
            'fidlock-picker__price--sale'
          );

          if (compareAtPriceHtml) {
            const previousPriceElement =
              document.createElement('span');

            previousPriceElement.className =
              'fidlock-picker__price-was';

            previousPriceElement.innerHTML =
              compareAtPriceHtml;

            priceElement.appendChild(
              previousPriceElement
            );
          }

          const discountPercentage =
            Math.round(
              (
                (
                  numericCompareAtPrice -
                  numericPrice
                ) /
                numericCompareAtPrice
              ) * 100
            );

          const badge =
            document.createElement('span');

          badge.className =
            'fidlock-picker__sale-badge';

          badge.textContent =
            `${discountPercentage}% off`;

          badge.setAttribute(
            'aria-hidden',
            'true'
          );

          button.appendChild(
            badge
          );

          const currentPriceText =
            this.stripHtml(
              currentPriceHtml
            );

          const compareAtPriceText =
            this.stripHtml(
              compareAtPriceHtml
            );

          const saleDescription =
            compareAtPriceText
              ? `${discountPercentage}% off, now ${currentPriceText}, was ${compareAtPriceText}`
              : `${discountPercentage}% off, now ${currentPriceText}`;

          button.setAttribute(
            'aria-label',
            `${button.dataset.baseAriaLabel}, ${saleDescription}`
          );
        }
      );
    }

    handleClick(event) {
      const button =
        event.target.closest(
          '[data-fidlock-variant]'
        );

      if (
        !button ||
        !this.contains(button)
      ) {
        return;
      }

      this.selectVariant(button);
    }

    selectVariant(button) {
      const styleValue =
        button.dataset.styleValue;

      const sizeValue =
        button.dataset.sizeValue;

      const styleInput =
        this.findNativeOptionInput(
          this.styleOptionName,
          styleValue
        );

      const sizeInput =
        this.findNativeOptionInput(
          this.sizeOptionName,
          sizeValue
        );

      if (
        !styleInput ||
        !sizeInput
      ) {
        console.warn(
          'The Fidlock variant picker could not find the matching native Shopify options.'
        );

        return;
      }

      styleInput.checked = true;
      sizeInput.checked = true;

      styleInput.dispatchEvent(
        new Event('change', {
          bubbles: true
        })
      );
    }

    findNativeOptionInput(
      optionName,
      optionValue
    ) {
      const selector =
        Array.from(
          this.variantPicker.querySelectorAll(
            '.option-selector[data-selector-type="listed"]'
          )
        ).find(
          (element) =>
            element.dataset.option ===
            optionName
        );

      if (!selector) return null;

      return (
        Array.from(
          selector.querySelectorAll(
            'input.js-option'
          )
        ).find(
          (input) =>
            input.value ===
            optionValue
        ) || null
      );
    }

    handleVariantChange(event) {
      const variant =
        event.detail &&
        event.detail.variant;

      if (!variant) return;

      this.updateSelectedState(
        String(variant.id)
      );
    }

    updateSelectedState(variantId) {
      let selectedButton = null;

      this.variantButtons.forEach(
        (button) => {
          const isSelected =
            button.dataset.variantId ===
            String(variantId);

          button.setAttribute(
            'aria-pressed',
            isSelected
              ? 'true'
              : 'false'
          );

          if (isSelected) {
            selectedButton = button;
          }
        }
      );

      if (
        !selectedButton ||
        !this.selectedText
      ) {
        return;
      }

      this.selectedText.textContent =
        `${selectedButton.dataset.styleValue} / ${selectedButton.dataset.sizeValue}`;
    }
  }

  customElements.define(
    'fidlock-variant-grid',
    FidlockVariantGrid
  );
}